import { FC } from "react";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { useFormContext } from "react-hook-form";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import { CattleFeed as CattleFeedBaseline } from "../../../../models/Baseline/CattleBaseline";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { Spacer } from "../../../helpers/Spacer";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { FormType } from "../../common";
import { BeefIntervention } from "../../../../models/Intervention/BeefIntervention";
import { CattleFeed } from "../../../../models/Intervention/CattleIntervention";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";

interface BeefFeedMothersMilkProps {
  formType: FormType;
  fieldItemPrefix: string;
  baselineStageData: CattleFeedBaseline;
  currentFormValues: CattleFeed | undefined | null;
  isMotherMilkEdit: boolean;
  setIsMotherMilkEdit: (val: boolean) => void;
}

const BeefFeedMothersMilk: FC<BeefFeedMothersMilkProps> = ({
  formType,
  fieldItemPrefix,
  baselineStageData,
  currentFormValues,
  isMotherMilkEdit,
  setIsMotherMilkEdit,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const fc = useFormContext<BeefIntervention>();
  const userProfile = UserProfilePrefs.getInstance();
  const userUOM = userProfile.getUserUnitPrefs();
  const originalBarnOutputMassUnit = unitLong(userUOM.unitBarnOutputMass as string);

  let barnOutputMassUnit: string;
  switch (originalBarnOutputMassUnit) {
    case "kg":
      barnOutputMassUnit = "kg DM";
      break;
    case "lbs":
      barnOutputMassUnit = "lbs DM";
      break;
    default:
      barnOutputMassUnit = originalBarnOutputMassUnit || "kg DM";
      break;
  }

  const cancelEditionHandler = () => {
    const mothersMilkPrefix = `${fieldItemPrefix}.mothersMilk`;
    const lastValues = currentFormValues?.mothersMilk;

    fc.setValue(`${mothersMilkPrefix}.amount`, lastValues?.amount ?? "");
    fc.setValue(
      `${mothersMilkPrefix}.dryMatterContent`,
      lastValues?.dryMatterContent ?? ""
    );
    fc.setValue(
      `${mothersMilkPrefix}.crudeProteinContent`,
      lastValues?.crudeProteinContent ?? ""
    );
    fc.setValue(
      `${mothersMilkPrefix}.digestibility`,
      lastValues?.digestibility ?? ""
    );

    setIsMotherMilkEdit(false);
  };

  return (
    <DsmGrid
      className={classes.dsmGridOneColumnWithBorder}
      style={{
        padding: "var(--dsm-spacing-px-4)",
      }}
    >
      {!isMotherMilkEdit && (
        <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
          {!!fc.getValues(`${fieldItemPrefix}.mothersMilk.amount`) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.mothersMilk.amount`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.AMOUNT",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.AMOUNT.TOOLTIP",
                })}
                type="number"
                metricUnit={barnOutputMassUnit}
                baseline={baselineStageData.mothersMilk?.amount}
                disabled
                displayTypeSelect={false}
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.mothersMilk.originalAmount`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.AMOUNT",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.AMOUNT.TOOLTIP",
              })}
              adornment={barnOutputMassUnit}
              type="number"
              defaultValue={baselineStageData.mothersMilk?.amount ?? ""}
              disabled
            />
          )}
          {!!fc.getValues(`${fieldItemPrefix}.mothersMilk.dryMatterContent`) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.mothersMilk.dryMatterContent`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT.TOOLTIP",
                })}
                type="number"
                metricUnit="%"
                baseline={baselineStageData.mothersMilk?.dryMatterContent}
                disabled
                displayTypeSelect={false}
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.mothersMilk.originalDryMatterContent`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT.TOOLTIP",
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT.ADORNMENT",
              })}
              placeholder={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT.PLACEHOLDER",
              })}
              type="number"
              defaultValue={
                baselineStageData.mothersMilk?.dryMatterContent ?? ""
              }
              disabled
            />
          )}
          {!!fc.getValues(
            `${fieldItemPrefix}.mothersMilk.crudeProteinContent`
          ) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.mothersMilk.crudeProteinContent`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT.TOOLTIP",
                })}
                type="number"
                metricUnit={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT.ADORNMENT",
                })}
                baseline={baselineStageData.mothersMilk?.crudeProteinContent}
                disabled
                displayTypeSelect={false}
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.mothersMilk.originalCrudeProteinContent`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT.TOOLTIP",
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT.ADORNMENT",
              })}
              placeholder={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT.PLACEHOLDER",
              })}
              type="number"
              defaultValue={
                baselineStageData.mothersMilk?.crudeProteinContent ?? ""
              }
              disabled
            />
          )}
          {!!fc.getValues(`${fieldItemPrefix}.mothersMilk.digestibility`) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.mothersMilk.digestibility`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY.TOOLTIP",
                })}
                type="number"
                metricUnit={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY.ADORNMENT",
                })}
                baseline={baselineStageData.mothersMilk?.digestibility}
                disabled
                displayTypeSelect={false}
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.mothersMilk.originalDigestibility`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY.TOOLTIP",
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY.ADORNMENT",
              })}
              placeholder={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY.PLACEHOLDER",
              })}
              type="number"
              defaultValue={baselineStageData.mothersMilk?.digestibility ?? ""}
              disabled
            />
          )}
        </DsmGrid>
      )}
      {isMotherMilkEdit && (
        <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.mothersMilk.amount`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.AMOUNT",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.AMOUNT.TOOLTIP",
              })}
              metricUnit={barnOutputMassUnit}
              type="number"
              baseline={baselineStageData.mothersMilk?.amount}
              disabled={false}
            />
            <Spacer />
          </div>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.mothersMilk.dryMatterContent`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT.TOOLTIP",
              })}
              metricUnit="%"
              type="number"
              baseline={baselineStageData.mothersMilk?.dryMatterContent}
              disabled={false}
            />
            <Spacer />
          </div>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.mothersMilk.crudeProteinContent`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT.TOOLTIP",
              })}
              metricUnit={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT.ADORNMENT",
              })}
              type="number"
              baseline={baselineStageData.mothersMilk?.crudeProteinContent}
              disabled={false}
            />
            <Spacer />
          </div>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.mothersMilk.digestibility`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY.TOOLTIP",
              })}
              metricUnit={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY.ADORNMENT",
              })}
              type="number"
              baseline={baselineStageData.mothersMilk?.digestibility}
              disabled={false}
            />
            <Spacer />
          </div>
        </DsmGrid>
      )}
      {formType !== FormType.View &&
        <EditModeButtons
          isEditing={isMotherMilkEdit}
          onEdit={() => setIsMotherMilkEdit(true)}
          onCancel={cancelEditionHandler}
        />}
    </DsmGrid>
  );
};

export default BeefFeedMothersMilk;
